import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AdminLayout from "@/layouts/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Packages from "@/pages/Packages";
import Departures from "@/pages/Departures";
import Bookings from "@/pages/Bookings";
import Payments from "@/pages/Payments";
import Customers from "@/pages/Customers";
import Agents from "@/pages/Agents";
import CustomerPortal from "@/pages/CustomerPortal";
import Storefront from "@/pages/Storefront";
import AgentPortal from "@/pages/AgentPortal";
import DepartureDetail from "@/pages/DepartureDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AIAssistant from "@/pages/AIAssistant";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand" data-testid="auth-loading">
        <div className="w-10 h-10 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/store" element={<Storefront />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/app" element={<Protected><AdminLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="packages" element={<Packages />} />
        <Route path="departures" element={<Departures />} />
        <Route path="departures/:departureId" element={<DepartureDetail />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="payments" element={<Payments />} />
        <Route path="customers" element={<Customers />} />
        <Route path="portal" element={<CustomerPortal />} />
        <Route path="agents" element={<Agents />} />
        <Route path="agent" element={<AgentPortal />} />
        <Route path="ai" element={<AIAssistant />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
